import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import dayjs from "dayjs";
import {
  Card,
  Table,
  Select,
  Input,
  InputNumber,
  DatePicker,
  Button,
  Space,
  Row,
  Col,
  Typography,
  Switch,
  Alert,
  Collapse,
  AutoComplete,
} from "antd";
import {
  PlusOutlined,
  SaveOutlined,
  ClearOutlined,
  ReloadOutlined,
  EditOutlined,
  DeleteOutlined,
  TruckOutlined,
  AppstoreOutlined,
  PercentageOutlined,
  HistoryOutlined,
  InboxOutlined,
  CheckCircleOutlined,
  SearchOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import fetchData from "../../functions/fetchData";
import { getHeaders } from "../../utility/getHeader";
import Layout from "../../layout/Layout";
import Header from "../../components/Header";
import "react-toastify/dist/ReactToastify.css";

const { Text } = Typography;
const { TextArea } = Input;

// Retail color palette - one accent color per section so the page reads at a glance
const ACCENT = {
  lookup: { color: "#7c3aed", bg: "#f5f3ff" },
  supplier: { color: "#2563eb", bg: "#eff6ff" },
  items: { color: "#0d9488", bg: "#f0fdfa" },
  vat: { color: "#f59e0b", bg: "#fffbeb" },
  history: { color: "#f59e0b", bg: "#fffbeb" },
};

const cardStyle = (accent) => ({ borderRadius: 10, marginBottom: 16, borderLeft: `4px solid ${accent.color}` });

const IconBadge = ({ icon, accent }) => (
  <span style={{
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 28,
    height: 28,
    borderRadius: 8,
    background: accent.bg,
    color: accent.color,
  }}>
    {icon}
  </span>
);

const StatBox = ({ label, value, icon, accent }) => (
  <div style={{
    background: accent.bg,
    border: `1px solid ${accent.color}33`,
    borderRadius: 10,
    padding: "12px 16px",
    display: "flex",
    alignItems: "center",
    gap: 12,
    height: "100%",
  }}>
    <div style={{
      width: 40,
      height: 40,
      flexShrink: 0,
      borderRadius: "50%",
      background: accent.color,
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 18,
    }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: 12, color: "#64748b" }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: accent.color }}>{value}</div>
    </div>
  </div>
);

export default function NewStock() {
  const navigate = useNavigate();

  // Refs for focusing
  const itemSelectRefs = React.useRef([]);

  // Stock entry state
  const [stockEntries, setStockEntries] = useState([{
    id: Date.now(),
    item_id: '',
    itemName: '',
    stock_in: '',
    unit: '',
    purchase_price: '',
    vat: '',
    subtotal: '',
    vatAmount: '',
    netAmount: '',
    refno: '',
    pdate: new Date().toISOString().split('T')[0]
  }]);

  const [currentEntry, setCurrentEntry] = useState(0);
  const [supplier, setSupplier] = useState(null);

  // Additional form fields
  const [billRefNo, setBillRefNo] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(new Date().toISOString().split('T')[0]);
  const [remarks, setRemarks] = useState('');
  const [billTotal, setBillTotal] = useState(0);
  const [discount, setDiscount] = useState(0);
  const [savedToStorage, setSavedToStorage] = useState(false);
  const [creditToLedger, setCreditToLedger] = useState(true);

  // Data states
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [stockHistory, setStockHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [autoVat, setAutoVat] = useState(true);
  const [vatPercentage] = useState(7);

  // Edit mode states
  const [isEditMode, setIsEditMode] = useState(false);
  const [editRefNo, setEditRefNo] = useState('');
  const [searchRefNo, setSearchRefNo] = useState('');
  const [refNoSuggestions, setRefNoSuggestions] = useState([]);


  // Note: This page intentionally skips token validation and interceptor-based auto-logout

  // Load initial data
  useEffect(() => {
    loadInitialData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load from local storage on component mount
  useEffect(() => {
    loadFromLocalStorage();
  }, []);

  // Update bill total whenever stock entries change
  useEffect(() => {
    const total = stockEntries.reduce((sum, entry) => {
      const netAmount = parseFloat(entry.netAmount) || 0;
      return sum + netAmount;
    }, 0);
    setBillTotal(total);
  }, [stockEntries]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleShortcut = (e) => {
      // F1 - Add New Entry
      if (e.key === 'F1') {
        e.preventDefault();
        addNewStockEntry();
      }
      // F2 - Save Bill to Database
      else if (e.key === 'F2') {
        e.preventDefault();
        saveBillToDatabase();
      }
      // F3 - Clear Form
      else if (e.key === 'F3') {
        e.preventDefault();
        clearForm();
      }
      // F4 - Refresh Data
      else if (e.key === 'F4') {
        e.preventDefault();
        loadStockHistory();
      }
      // Ctrl+Enter - Add New Row
      else if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
        e.preventDefault();
        addNewStockEntry();
      }
      // Ctrl+Delete - Remove Last Row
      else if (e.ctrlKey && (e.key === 'Delete' || e.key === 'Del')) {
        e.preventDefault();
        removeLastEntry();
      }
    };

    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInitialData = useCallback(async () => {
    setLoading(true);
    try {
      // Load items from items table and suppliers
      const [itemsData, suppliersData] = await Promise.all([
        fetchData('items', null, 'id', {'isstockable': '1'}),
        fetchData('suppliers', null, 'id', {})
      ]);

      // Filter items for stockable ones only
      const stockableItems = (itemsData || []).filter(item =>
        item.isstockable === '1' || item.isstockable === 1 || item.isstockable === true
      );

      // Set state
      setItems(stockableItems);
      setSuppliers(suppliersData || []);

      if (stockableItems.length === 0) {
        toast.warning('No stockable items found. Please check item settings.');
      }

      // Load recent stock history
      await loadStockHistory();
    } catch (error) {
      console.error('Error loading initial data:', error);
      toast.error('Failed to load initial data: ' + error.message);
    }
    setLoading(false);
  }, []);

  const loadStockHistory = async () => {
    try {
      const response = await axios.get(
        '/inventory/details', getHeaders()
      );

      if (response.data.success) {
        const history = response.data.data || [];
        setStockHistory(history);

        // Extract unique reference numbers for autocomplete
        const uniqueRefNos = [...new Set(history.map(item => item.refno).filter(Boolean))];
        setRefNoSuggestions(uniqueRefNos.sort((a, b) => b.localeCompare(a))); // Sort descending
      }
    } catch (error) {
      console.error('Error loading stock history:', error);
    }
  };

  // Local Storage Functions
  const saveToLocalStorage = () => {
    const billData = {
      stockEntries: stockEntries.filter(entry => entry.item_id && entry.stock_in),
      supplier: supplier,
      billRefNo: billRefNo,
      invoiceDate: invoiceDate,
      remarks: remarks,
      discount: discount,
      timestamp: new Date().toISOString()
    };

    localStorage.setItem('pendingStockBill', JSON.stringify(billData));
    setSavedToStorage(true);
    toast.success('Bill saved to local storage successfully!');
  };

  const loadFromLocalStorage = () => {
    const savedBill = localStorage.getItem('pendingStockBill');
    if (savedBill) {
      const billData = JSON.parse(savedBill);
      setStockEntries(billData.stockEntries || []);
      setSupplier(billData.supplier || null);
      setBillRefNo(billData.billRefNo || '');
      setInvoiceDate(billData.invoiceDate || new Date().toISOString().split('T')[0]);
      setRemarks(billData.remarks || '');
      setDiscount(billData.discount || 0);
      setSavedToStorage(true);
      toast.info('Loaded bill from local storage');
    }
  };

  const clearLocalStorage = () => {
    localStorage.removeItem('pendingStockBill');
    setSavedToStorage(false);
  };

  const addNewStockEntry = () => {
    const newEntry = {
      id: Date.now(),
      item_id: '',
      itemName: '',
      stock_in: '',
      unit: '',
      purchase_price: '',
      vat: '',
      subtotal: '',
      vatAmount: '',
      netAmount: '',
      refno: '',
      pdate: new Date().toISOString().split('T')[0]
    };

    setStockEntries(prev => [...prev, newEntry]);
    setCurrentEntry(stockEntries.length);

    // Auto-focus on the new row's item select after a brief delay
    setTimeout(() => {
      const newIndex = stockEntries.length;
      if (itemSelectRefs.current[newIndex]) {
        itemSelectRefs.current[newIndex].focus();
      }
    }, 100);
  };

  const removeLastEntry = () => {
    if (stockEntries.length > 1) {
      setStockEntries(prev => prev.slice(0, -1));
      setCurrentEntry(Math.max(0, currentEntry - 1));
    }
  };

  const clearForm = () => {
    setStockEntries([{
      id: Date.now(),
      item_id: '',
      itemName: '',
      stock_in: '',
      unit: '',
      purchase_price: '',
      vat: '',
      subtotal: '',
      vatAmount: '',
      netAmount: '',
      refno: '',
      pdate: new Date().toISOString().split('T')[0]
    }]);
    setCurrentEntry(0);
    setSupplier(null);
    setBillRefNo('');
    setInvoiceDate(new Date().toISOString().split('T')[0]);
    setRemarks('');
    setDiscount(0);
    clearLocalStorage();
    toast.info('Form cleared');
  };

  const handleItemSelect = (selectedOption, entryIndex) => {
    if (!selectedOption) return;

    const item = items.find(i => i.id === selectedOption.value);
    if (!item) return;

    setStockEntries(prev => prev.map((entry, index) =>
      index === entryIndex ? {
        ...entry,
        item_id: item.id,
        itemName: selectedOption.label,
        unit: item.unit || '',
        purchase_price: item.offerprice || item.mrp || '',
      } : entry
    ));

    // Auto-calculate if purchase price exists
    const price = item.offerprice || item.mrp;
    if (price) {
      calculateAmounts(entryIndex, 'purchase_price', price);
    }
  };

  const handleInputChange = (entryIndex, field, value) => {
    setStockEntries(prev => prev.map((entry, index) =>
      index === entryIndex ? { ...entry, [field]: value } : entry
    ));

    // Auto-calculate amounts when relevant fields change
    if (['purchase_price', 'stock_in', 'vat'].includes(field)) {
      calculateAmounts(entryIndex, field, value);
    }
  };

  const calculateAmounts = (entryIndex, changedField, value) => {
    setStockEntries(prev => prev.map((entry, index) => {
      if (index !== entryIndex) return entry;

      const updatedEntry = { ...entry, [changedField]: value };
      const stockIn = parseFloat(updatedEntry.stock_in) || 0;
      const purchasePrice = parseFloat(updatedEntry.purchase_price) || 0;
      const vatRate = autoVat ? vatPercentage : (parseFloat(updatedEntry.vat) || 0);

      if (stockIn > 0 && purchasePrice > 0) {
        const subtotal = stockIn * purchasePrice;
        const vatAmount = (subtotal * vatRate) / 100;
        const netAmount = subtotal + vatAmount;

        return {
          ...updatedEntry,
          subtotal: subtotal.toFixed(2),
          vat: vatRate.toString(),
          vatAmount: vatAmount.toFixed(2),
          netAmount: netAmount.toFixed(2)
        };
      }

      return updatedEntry;
    }));
  };

  // Save Bill to Database
  const saveBillToDatabase = async () => {
    if (!supplier) {
      toast.error('Please select a supplier');
      return;
    }

    const validEntries = stockEntries.filter(entry => entry.item_id && entry.stock_in);

    if (validEntries.length === 0) {
      toast.error('Please add at least one valid item');
      return;
    }

    if (!billRefNo.trim()) {
      toast.error('Please enter Reference Number');
      return;
    }

    try {
      const billData = {
        supplier_id: supplier.value,
        description: remarks || `Purchase from ${supplier.label}`,
        credit_to_ledger: creditToLedger,
        items: validEntries.map(entry => ({
          item_id: entry.item_id,
          stock_in: parseFloat(entry.stock_in),
          unit: entry.unit || '',
          refno: billRefNo,
          pdate: invoiceDate,
          purchase_price: parseFloat(entry.purchase_price),
          vat: parseFloat(entry.vat) || 0,
          subtotal: parseFloat(entry.subtotal) || 0,
          vatAmount: parseFloat(entry.vatAmount) || 0,
          netAmount: parseFloat(entry.netAmount)
        }))
      };

      const response = await axios.post(
        '/inventory/bulk-insert',
        billData,
        getHeaders()
      );

      if (response.data.success) {
        toast.success('Bill saved successfully!');
        clearLocalStorage();
        clearForm();
        setBillRefNo('');
        setInvoiceDate(new Date().toISOString().split('T')[0]);
        setRemarks('');
        setDiscount(0);
        setSupplier(null);
        setCreditToLedger(true);
        await loadStockHistory();
      } else {
        throw new Error(response.data.message || 'Failed to save bill');
      }
    } catch (error) {
      console.error('Error saving bill:', error);

      // Check for 401 authentication error
      if (error.response?.status === 401) {
        toast.error('Authentication failed. Please login again.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else if (error.code === 'ERR_NETWORK') {
        toast.error('Network error. Please check your connection and try again.');
      } else if (error.response) {
        toast.error(error.response?.data?.message || `Error: ${error.response.status} - ${error.response.statusText}`);
      } else if (error.request) {
        toast.error('No response from server. Please check if backend is running.');
      } else {
        toast.error(error.message || 'Failed to save bill to database');
      }
    }
  };

  // Handle reference number input change
  const handleRefNoChange = (value) => {
    setSearchRefNo(value);
  };

  // Select suggestion
  const selectRefNoSuggestion = (refNo) => {
    setSearchRefNo(refNo);
  };

  // Load inventory for editing by reference number
  const loadInventoryForEdit = async () => {
    if (!searchRefNo.trim()) {
      toast.error('Please enter Reference Number');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(
        `/inventory/by-refno/${searchRefNo.trim()}`,
        getHeaders()
      );

      if (response.data.success) {
        const { items: inventoryItems, supplier_id, supplier_name, reference_number, purchase_date } = response.data.data;

        // Set supplier
        const supplierOption = suppliers.find(s => s.id === supplier_id);
        if (supplierOption) {
          setSupplier({
            value: supplier_id,
            label: supplierOption.supplier_name || supplierOption.company_name || `Supplier ${supplier_id}`
          });
        }

        // Set bill details
        setBillRefNo(reference_number);
        setInvoiceDate(purchase_date ? new Date(purchase_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]);

        // Load items into stock entries
        const loadedEntries = inventoryItems.map((item, index) => ({
          id: Date.now() + index,
          item_id: item.item_id,
          itemName: `${item.item_code} - ${item.item_name}`,
          stock_in: item.stock_in,
          unit: item.unit,
          purchase_price: item.purchase_price,
          vat: item.vat || 7,
          subtotal: item.subtotal,
          vatAmount: item.vatAmount,
          netAmount: item.netAmount,
          refno: reference_number,
          pdate: purchase_date ? new Date(purchase_date).toISOString().split('T')[0] : new Date().toISOString().split('T')[0]
        }));

        setStockEntries(loadedEntries);
        setIsEditMode(true);
        setEditRefNo(reference_number);
        toast.success(`Loaded ${inventoryItems.length} items for editing from Ref: ${reference_number}`);
      }
    } catch (error) {
      console.error('Error loading inventory:', error);
      toast.error(error.response?.data?.message || 'Failed to load inventory for editing');
    }
    setLoading(false);
  };

  // Save edited inventory
  const saveEditedInventory = async () => {
    if (!supplier) {
      toast.error('Please select a supplier');
      return;
    }

    const validEntries = stockEntries.filter(entry => entry.item_id && entry.stock_in);
    if (validEntries.length === 0) {
      toast.error('Please add at least one valid item');
      return;
    }

    if (!editRefNo.trim()) {
      toast.error('Reference number is missing');
      return;
    }

    // For single item edit - send item_id and stock_in directly
    if (validEntries.length === 1) {
      setLoading(true);
      try {
        const entry = validEntries[0];
        const editData = {
          supplier_id: supplier.value,
          item_id: entry.item_id,
          stock_in: parseFloat(entry.stock_in),
          unit: entry.unit || '',
          pdate: invoiceDate,
          purchase_price: parseFloat(entry.purchase_price),
          vat: parseFloat(entry.vat) || 0,
          subtotal: parseFloat(entry.subtotal) || 0,
          vatAmount: parseFloat(entry.vatAmount) || 0,
          netAmount: parseFloat(entry.netAmount)
        };

        const response = await axios.put(
          `/inventory/edit-by-refno/${editRefNo}`,
          editData,
          getHeaders()
        );

        if (response.data.success) {
          toast.success('Inventory updated successfully!');
          cancelEditMode();
          await loadStockHistory();
        } else {
          throw new Error(response.data.message || 'Failed to update inventory');
        }
      } catch (error) {
        console.error('Error updating inventory:', error);
        if (error.response?.status === 401) {
          toast.error('Authentication failed. Please login again.');
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        } else {
          toast.error(error.response?.data?.message || 'Failed to update inventory');
        }
      }
      setLoading(false);
    } else {
      // For multiple items - use bulk edit endpoint
      setLoading(true);
      try {
        const bulkData = {
          supplier_id: supplier.value,
          reference_number: editRefNo,
          items: validEntries.map(entry => ({
            item_id: entry.item_id,
            stock_in: parseFloat(entry.stock_in),
            unit: entry.unit || '',
            pdate: invoiceDate,
            purchase_price: parseFloat(entry.purchase_price),
            vat: parseFloat(entry.vat) || 0,
            subtotal: parseFloat(entry.subtotal) || 0,
            vatAmount: parseFloat(entry.vatAmount) || 0,
            netAmount: parseFloat(entry.netAmount)
          }))
        };

        const response = await axios.put(
          `/inventory/bulk-edit-by-refno/${editRefNo}`,
          bulkData,
          getHeaders()
        );

        if (response.data.success) {
          toast.success(`Inventory updated successfully! ${validEntries.length} items saved.`);
          cancelEditMode();
          await loadStockHistory();
        } else {
          throw new Error(response.data.message || 'Failed to update inventory');
        }
      } catch (error) {
        console.error('Error updating inventory (bulk):', error);
        if (error.response?.status === 401) {
          toast.error('Authentication failed. Please login again.');
          setTimeout(() => {
            navigate('/login');
          }, 2000);
        } else {
          toast.error(error.response?.data?.message || 'Failed to update inventory. Try editing items one at a time.');
        }
      }
      setLoading(false);
    }
  };

  // Cancel edit mode
  const cancelEditMode = () => {
    if (window.confirm('Cancel editing? All changes will be lost.')) {
      setIsEditMode(false);
      setEditRefNo('');
      setSearchRefNo('');
      clearForm();
      toast.info('Edit cancelled');
    }
  };

  const handleEditStock = (stock) => {
    // Implementation for editing stock entry
  };

  const handleDeleteStock = async (stockId) => {
    if (!window.confirm('Are you sure you want to delete this stock entry?')) return;

    try {
      const response = await axios.delete(
        `/inventory/${stockId}`,
        {
          ...getHeaders(),
          data: { reason: 'User requested deletion' }
        }
      );

      if (response.data.success) {
        toast.success('Stock entry deleted successfully');
        loadStockHistory();
      } else {
        toast.error('Failed to delete stock entry');
      }
    } catch (error) {
      console.error('Error deleting stock entry:', error);
      toast.error('Failed to delete stock entry');
    }
  };

  // Format options for antd Select
  const itemOptions = items.map(item => ({
    value: item.id,
    label: `${item.item_code} - ${item.iname}`,
    unit: item.unit,
    purchase_price: item.offerprice || item.mrp || 0
  }));

  const supplierOptions = suppliers.map(supplier => ({
    value: supplier.id,
    label: supplier.supplier_name || `Supplier ${supplier.id}`
  }));

  const refNoOptions = refNoSuggestions
    .filter(refNo => refNo.toLowerCase().includes(searchRefNo.toLowerCase()))
    .map(refNo => ({ value: refNo }));

  // Calculate totals for all entries
  const calculateTotals = () => {
    const validEntries = stockEntries.filter(entry => parseFloat(entry.netAmount) > 0);
    const totalAmount = validEntries.reduce((sum, entry) => sum + parseFloat(entry.netAmount || 0), 0);
    const totalVAT = validEntries.reduce((sum, entry) => sum + parseFloat(entry.vatAmount || 0), 0);
    const totalSubtotal = validEntries.reduce((sum, entry) => sum + parseFloat(entry.subtotal || 0), 0);

    return { totalAmount, totalVAT, totalSubtotal, itemCount: validEntries.length };
  };

  const totals = calculateTotals();

  const stockColumns = [
    {
      title: "Item",
      key: "item",
      width: 240,
      render: (_, entry, index) => (
        <Select
          ref={el => (itemSelectRefs.current[index] = el)}
          showSearch
          value={entry.item_id || undefined}
          onChange={(_value, option) => handleItemSelect(option, index)}
          options={itemOptions}
          placeholder={itemOptions.length > 0 ? `Select from ${itemOptions.length} items...` : "No items available"}
          style={{ width: "100%" }}
          status={itemOptions.length === 0 ? "error" : undefined}
          filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
        />
      ),
    },
    {
      title: "Stock In",
      key: "stock_in",
      width: 100,
      render: (_, entry, index) => (
        <InputNumber
          style={{ width: "100%" }}
          value={entry.stock_in}
          onChange={(v) => handleInputChange(index, 'stock_in', v ?? '')}
          placeholder="0"
        />
      ),
    },
    {
      title: "Unit",
      key: "unit",
      width: 80,
      render: (_, entry, index) => (
        <Input
          value={entry.unit}
          onChange={(e) => handleInputChange(index, 'unit', e.target.value)}
          placeholder="Unit"
        />
      ),
    },
    {
      title: "Price",
      key: "purchase_price",
      width: 110,
      render: (_, entry, index) => (
        <InputNumber
          style={{ width: "100%" }}
          value={entry.purchase_price}
          onChange={(v) => handleInputChange(index, 'purchase_price', v ?? '')}
          placeholder="0.00"
          step={0.01}
        />
      ),
    },
    {
      title: "VAT%",
      key: "vat",
      width: 80,
      render: (_, entry, index) => (
        <InputNumber
          style={{ width: "100%" }}
          value={entry.vat}
          onChange={(v) => handleInputChange(index, 'vat', v ?? '')}
          placeholder="7"
          disabled={autoVat}
        />
      ),
    },
    {
      title: "Net Amount",
      key: "netAmount",
      width: 110,
      align: "right",
      render: (_, entry) => (
        <Text strong style={{ color: "#0d9488" }}>
          ฿{parseFloat(entry.netAmount || 0).toFixed(2)}
        </Text>
      ),
    },
    {
      title: "Ref No",
      key: "refno",
      width: 110,
      render: (_, entry, index) => (
        <Input
          value={entry.refno}
          onChange={(e) => handleInputChange(index, 'refno', e.target.value)}
          placeholder="Ref#"
        />
      ),
    },
    {
      title: "Date",
      key: "pdate",
      width: 130,
      render: (_, entry, index) => (
        <DatePicker
          style={{ width: "100%" }}
          format="YYYY-MM-DD"
          value={entry.pdate ? dayjs(entry.pdate) : null}
          onChange={(_, dateString) => handleInputChange(index, 'pdate', dateString || '')}
        />
      ),
    },
    {
      title: "",
      key: "action",
      width: 50,
      render: (_, entry, index) => (
        stockEntries.length > 1 && (
          <Button
            danger
            size="small"
            icon={<DeleteOutlined />}
            onClick={() => setStockEntries(prev => prev.filter((_, i) => i !== index))}
          />
        )
      ),
    },
  ];

  const historyColumns = [
    {
      title: "Item",
      key: "item",
      render: (_, stock) => (
        <>
          <div style={{ fontWeight: 500 }}>{stock.item_name || 'N/A'}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>{stock.item_code}</Text>
        </>
      ),
    },
    {
      title: "Stock In",
      key: "stock_in",
      render: (_, stock) => (
        <>
          <span style={{ fontWeight: 500 }}>{stock.stock_in}</span>
          <Text type="secondary" style={{ fontSize: 12, display: "block" }}>{stock.unit}</Text>
        </>
      ),
    },
    {
      title: "Purchase Price",
      key: "purchase_price",
      render: (_, stock) => `฿${parseFloat(stock.purchase_price || 0).toFixed(2)}`,
    },
    {
      title: "Net Amount",
      key: "netAmount",
      render: (_, stock) => <Text strong style={{ color: "#0d9488" }}>฿{parseFloat(stock.netAmount || 0).toFixed(2)}</Text>,
    },
    {
      title: "Date",
      key: "pdate",
      render: (_, stock) => new Date(stock.pdate).toLocaleDateString(),
    },
    {
      title: "Supplier",
      dataIndex: "supplier_name",
      key: "supplier_name",
      render: (v) => v || 'N/A',
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, stock) => (
        <Space size={6}>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEditStock(stock)} title="Edit" />
          <Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleDeleteStock(stock.id)} title="Delete" />
        </Space>
      ),
    },
  ];

  const canSaveNew = !loading && supplier && billRefNo.trim() && stockEntries.filter(e => e.item_id && e.stock_in).length > 0;
  const canSaveEdit = !loading && supplier && stockEntries.filter(e => e.item_id && e.stock_in).length > 0;

  const renderContent = () => (
    <div>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Quick edit lookup - promoted to the top so finding an existing bill doesn't require scrolling */}
      <Card
        size="small"
        title={<Space><IconBadge icon={<SearchOutlined />} accent={ACCENT.lookup} /> Quick Edit Lookup</Space>}
        style={cardStyle(ACCENT.lookup)}
      >
        {!isEditMode ? (
          <Row gutter={[12, 12]} align="middle">
            <Col flex="auto">
              <AutoComplete
                style={{ width: "100%" }}
                value={searchRefNo}
                options={refNoOptions}
                onChange={handleRefNoChange}
                onSelect={selectRefNoSuggestion}
                onKeyDown={(e) => { if (e.key === 'Enter') loadInventoryForEdit(); }}
                placeholder="Search a reference number to edit an existing stock bill..."
              />
            </Col>
            <Col>
              <Button icon={<EditOutlined />} onClick={loadInventoryForEdit} loading={loading}>
                Load for Edit
              </Button>
            </Col>
          </Row>
        ) : (
          <Alert
            type="success"
            showIcon
            icon={<CheckCircleOutlined />}
            message={`Editing Inventory — Ref: ${editRefNo}`}
            description='Make changes to the items below, then click "Save Changes".'
            action={<Button danger size="small" onClick={cancelEditMode}>Cancel Edit</Button>}
          />
        )}
      </Card>

      {/* Supplier & Bill Information */}
      <Card
        title={<Space><IconBadge icon={<TruckOutlined />} accent={ACCENT.supplier} /> Supplier & Bill Information</Space>}
        style={cardStyle(ACCENT.supplier)}
      >
        <Row gutter={16}>
          <Col xs={24} md={8}>
            <Text type="secondary" style={{ fontSize: 12 }}>Supplier *</Text>
            <Select
              showSearch
              style={{ width: "100%" }}
              value={supplier?.value}
              onChange={(_value, option) => setSupplier(option || null)}
              options={supplierOptions}
              placeholder="Choose supplier..."
              filterOption={(input, option) => option.label.toLowerCase().includes(input.toLowerCase())}
            />
            {suppliers.length === 0 && (
              <Text type="danger" style={{ fontSize: 12 }}>No suppliers found</Text>
            )}
          </Col>
          <Col xs={24} md={8}>
            <Text type="secondary" style={{ fontSize: 12 }}>Reference No. *</Text>
            <Input
              value={billRefNo}
              onChange={(e) => setBillRefNo(e.target.value)}
              placeholder="Enter ref number..."
            />
          </Col>
          <Col xs={24} md={8}>
            <Text type="secondary" style={{ fontSize: 12 }}>Invoice Date</Text>
            <DatePicker
              style={{ width: "100%" }}
              format="YYYY-MM-DD"
              value={invoiceDate ? dayjs(invoiceDate) : null}
              onChange={(_, dateString) => setInvoiceDate(dateString || '')}
            />
          </Col>
        </Row>

        <Row gutter={16} style={{ marginTop: 16 }}>
          <Col xs={24} md={6}>
            <Text type="secondary" style={{ fontSize: 12 }}>Discount</Text>
            <InputNumber
              style={{ width: "100%" }}
              value={discount}
              onChange={(v) => setDiscount(v ?? 0)}
              placeholder="0.00"
              step={0.01}
              prefix="฿"
            />
          </Col>
          <Col xs={24} md={6}>
            <Text type="secondary" style={{ fontSize: 12 }}>Total Amount</Text>
            <div>
              <Text strong style={{ fontSize: 20, color: "#0d9488" }}>
                ฿{(billTotal - discount).toFixed(2)}
              </Text>
            </div>
          </Col>
          <Col xs={24} md={12}>
            <Text type="secondary" style={{ fontSize: 12 }}>Credit to Supplier Ledger</Text>
            <div>
              <Switch checked={creditToLedger} onChange={setCreditToLedger} />
              <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                {creditToLedger ? 'Amount will be added to ledger' : 'Only stock will be added to inventory'}
              </Text>
            </div>
          </Col>
        </Row>

        <Row style={{ marginTop: 16 }}>
          <Col span={24}>
            <Text type="secondary" style={{ fontSize: 12 }}>Remarks</Text>
            <TextArea
              rows={2}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Enter any remarks or notes..."
            />
          </Col>
        </Row>
      </Card>

      {/* Stock Items - the Add button lives right next to the table it affects */}
      <Card
        title={<Space><IconBadge icon={<InboxOutlined />} accent={ACCENT.items} /> Stock Items</Space>}
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={addNewStockEntry}>
            Add Item (F1)
          </Button>
        }
        style={cardStyle(ACCENT.items)}
      >
        <Text type="secondary" style={{ fontSize: 12 }}>
          Ctrl+Enter to add row · Ctrl+Del to remove last row
        </Text>
        <Table
          style={{ marginTop: 12 }}
          dataSource={stockEntries}
          columns={stockColumns}
          rowKey="id"
          pagination={false}
          scroll={{ x: true }}
        />

        <Row gutter={16} style={{ marginTop: 20, paddingTop: 16, borderTop: "1px solid #f0f0f0" }}>
          <Col xs={12} md={6}>
            <StatBox label="Total Items" value={totals.itemCount} icon={<AppstoreOutlined />} accent={ACCENT.lookup} />
          </Col>
          <Col xs={12} md={6}>
            <StatBox label="Subtotal" value={`฿${totals.totalSubtotal.toFixed(2)}`} icon={<InboxOutlined />} accent={ACCENT.supplier} />
          </Col>
          <Col xs={12} md={6}>
            <StatBox label="Total VAT" value={`฿${totals.totalVAT.toFixed(2)}`} icon={<PercentageOutlined />} accent={ACCENT.vat} />
          </Col>
          <Col xs={12} md={6}>
            <StatBox label="Net Amount" value={`฿${totals.totalAmount.toFixed(2)}`} icon={<DollarOutlined />} accent={ACCENT.items} />
          </Col>
        </Row>
      </Card>

      {/* Recent Stock History - tucked away in a collapsible panel since it's reference info, not the active task */}
      <Collapse
        style={{ marginBottom: 16, borderLeft: `4px solid ${ACCENT.history.color}`, borderRadius: 10 }}
        items={[{
          key: "history",
          label: <Space><IconBadge icon={<HistoryOutlined />} accent={ACCENT.history} /> Recent Stock Entries ({stockHistory.length})</Space>,
          children: (
            <Table
              dataSource={stockHistory.slice(0, 20)}
              columns={historyColumns}
              rowKey="id"
              pagination={false}
              scroll={{ x: true }}
              locale={{ emptyText: "No stock entries found" }}
            />
          ),
        }]}
      />

      {/* Sticky action bar - always reachable regardless of how long the page gets */}
      <div style={{
        position: "sticky",
        bottom: 0,
        zIndex: 100,
        background: "#fff",
        borderTop: "1px solid #e5e7eb",
        borderRadius: 10,
        padding: "12px 16px",
        boxShadow: "0 -2px 8px rgba(0,0,0,0.06)",
      }}>
        <Row justify="space-between" align="middle" gutter={[12, 12]}>
          <Col>
            <Space wrap>
              {!isEditMode ? (
                <Button
                  type="primary"
                  size="large"
                  icon={<SaveOutlined />}
                  onClick={saveBillToDatabase}
                  disabled={!canSaveNew}
                >
                  Save Bill (F2)
                </Button>
              ) : (
                <Button
                  type="primary"
                  size="large"
                  icon={<SaveOutlined />}
                  onClick={saveEditedInventory}
                  disabled={!canSaveEdit}
                >
                  Save Changes
                </Button>
              )}
              <Button
                icon={<ClearOutlined />}
                danger={isEditMode}
                onClick={isEditMode ? cancelEditMode : clearForm}
                style={isEditMode ? undefined : { color: ACCENT.vat.color, borderColor: ACCENT.vat.color }}
              >
                {isEditMode ? 'Cancel Edit' : 'Clear (F3)'}
              </Button>
              <Button
                icon={<ReloadOutlined />}
                onClick={loadStockHistory}
                style={{ color: ACCENT.supplier.color, borderColor: ACCENT.supplier.color }}
              >
                Refresh (F4)
              </Button>
            </Space>
          </Col>
          {!isEditMode && (
            <Col>
              <Space>
                <Switch checked={autoVat} onChange={setAutoVat} />
                <Text style={{ fontSize: 13 }}>Auto VAT ({vatPercentage}%)</Text>
              </Space>
            </Col>
          )}
        </Row>
      </div>
    </div>
  );

  // Main render with proper Layout structure
  return (
    <Layout>
      <Header title="Stock Management" />
      <div style={{ padding: 20 }}>
        {renderContent()}
      </div>
    </Layout>
  );
}
